import { createSlice } from '@reduxjs/toolkit';
import {
	type SustainabilityFormDto,
	type SustainabilityFormSectionSupportingDocumentDto,
	SustainabilityFormStatus
} from '../../models/sustainability';
import sustainabilityApi from '../apis/sustainability.api';

export {
	selectAnswersByFormId,
	selectAnswerByQuestionId,
	selectFormByFormId,
	selectAnswerSections,
	selectAppliciableSection,
	selectLatestInProgressFormByProfileId,
	selectAnswersBySection,
	selectSectionStatus,
	selectCanSubmitFormForApproval
} from './sustainability-forms.selectors';
export type { SectionStatus } from './sustainability-forms.selectors';

const initialState: {
	profileForms: Record<number, Record<number, SustainabilityFormDto>>;
} = {
	profileForms: {}
};

function setOrAddAnswer(form: SustainabilityFormDto, questionId: number, answer: string): void {
	const existing = form.answers?.find((a) => a.sustainabilityQuestionId === questionId);
	if (existing) {
		if (existing.answer === answer) {
			form.answers = form.answers!.filter((a) => a.sustainabilityQuestionId !== questionId);
		} else {
			existing.answer = answer;
		}
	} else {
		if (!form.answers) form.answers = [];
		form.answers.push({ sustainabilityQuestionId: questionId, answer });
	}
}

const sustainabilityFormsSlice = createSlice({
	name: 'sustainabilityForms',
	initialState,
	reducers: {
		answerQuestion(
			state,
			action: {
				payload: {
					profileId: number;
					formId: number;
					questionId: number;
					answer: string;
				};
			}
		) {
			const { profileId, formId, questionId, answer } = action.payload;

			const form = state.profileForms[profileId]?.[formId];

			if (form) setOrAddAnswer(form, questionId, answer);
		},
		updateForm(state, action: { payload: { form: SustainabilityFormDto } }) {
			const { form } = action.payload;
			const profileId = form.profileId;
			if (!state.profileForms[profileId]) state.profileForms[profileId] = {};
			state.profileForms[profileId][form.id] = form;
		},
		appendSectionSupportingDocument(
			state,
			action: {
				payload: {
					profileId: number;
					formId: number;
					document: SustainabilityFormSectionSupportingDocumentDto;
				};
			}
		) {
			const { profileId, formId, document } = action.payload;
			const form = state.profileForms[profileId]?.[formId];
			if (!form) return;
			if (!form.sectionSupportingDocuments) {
				form.sectionSupportingDocuments = [];
			}
			form.sectionSupportingDocuments.push(document);
		},
		clearForm(state, action: { payload: { profileId: number; formId: number } }) {
			const { profileId, formId } = action.payload;
			if (state.profileForms[profileId]) {
				delete state.profileForms[profileId][formId];
			}
		},
		clearFormsByProfileId(state, action: { payload: { profileId: number } }) {
			const { profileId } = action.payload;
			if (state.profileForms[profileId]) {
				delete state.profileForms[profileId];
			}
		},
		updateAnswerSectionIsApplicable(
			state,
			action: {
				payload: {
					profileId: number;
					formId: number;
					section: string;
					isApplicable: boolean;
				};
			}
		) {
			const { profileId, formId, section, isApplicable } = action.payload;
			const form = state.profileForms[profileId]?.[formId];
			if (!form) return;
			if (!form.answerSections) form.answerSections = [];
			const existing = form.answerSections.find((s) => s.section === section);
			if (existing) {
				existing.isApplicable = isApplicable;
			} else {
				form.answerSections.push({
					section,
					status: '',
					isApplicable
				});
			}
		},
		clearAllForms(state) {
			state.profileForms = {};
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				sustainabilityApi.endpoints.cancelFormWaitingForApproval.matchFulfilled,
				(state, action) => {
					// update status
					const profileId = action.meta.arg.originalArgs.profileId;
					const formId = action.meta.arg.originalArgs.formId;
					state.profileForms[profileId][formId].status = SustainabilityFormStatus.InProgress;
				}
			)
			.addMatcher(
				sustainabilityApi.endpoints.sendFormForApproval.matchFulfilled,
				(state, action) => {
					// update status
					const profileId = action.meta.arg.originalArgs.profileId;
					const formId = action.meta.arg.originalArgs.request.formId;
					state.profileForms[profileId][formId].status =
						SustainabilityFormStatus.WaitingForApproval;
				}
			)
			.addMatcher(sustainabilityApi.endpoints.getForm.matchFulfilled, (state, action) => {
				const form = action.payload;
				if (
					form?.id !== undefined &&
					form?.id !== null &&
					form?.profileId !== undefined &&
					form?.profileId !== null
				) {
					if (!state.profileForms[form.profileId]) state.profileForms[form.profileId] = {};
					state.profileForms[form.profileId][form.id] = form;
				}
			})
			.addMatcher(sustainabilityApi.endpoints.createForm.matchFulfilled, (state, action) => {
				const form = action.payload;
				if (
					form?.id !== undefined &&
					form?.id !== null &&
					form?.profileId !== undefined &&
					form?.profileId !== null
				) {
					if (!state.profileForms[form.profileId]) state.profileForms[form.profileId] = {};
					state.profileForms[form.profileId][form.id] = form;
				}
			})
			.addMatcher(sustainabilityApi.endpoints.updateForm.matchFulfilled, (state, action) => {
				const form = action.payload;
				if (
					form?.id !== undefined &&
					form?.id !== null &&
					form?.profileId !== undefined &&
					form?.profileId !== null
				) {
					if (!state.profileForms[form.profileId]) state.profileForms[form.profileId] = {};
					state.profileForms[form.profileId][form.id] = form;
				}
			})
			.addMatcher(
				sustainabilityApi.endpoints.getLatestInProgressForm.matchFulfilled,
				(state, action) => {
					const form = action.payload;
					if (
						form?.id !== undefined &&
						form?.id !== null &&
						form?.profileId !== undefined &&
						form?.profileId !== null
					) {
						if (!state.profileForms[form.profileId]) state.profileForms[form.profileId] = {};
						state.profileForms[form.profileId][form.id] = form;
					}
				}
			)
			.addMatcher(sustainabilityApi.endpoints.getLatestForm.matchFulfilled, (state, action) => {
				const form = action.payload;
				if (
					form?.id !== undefined &&
					form?.id !== null &&
					form?.profileId !== undefined &&
					form?.profileId !== null
				) {
					if (!state.profileForms[form.profileId]) state.profileForms[form.profileId] = {};
					state.profileForms[form.profileId][form.id] = form;
				}
			})
			.addMatcher(
				sustainabilityApi.endpoints.removeSupportingDocument.matchFulfilled,
				(state, action) => {
					const { profileId, formId, documentId } = action.meta.arg.originalArgs;
					const form = state.profileForms[profileId]?.[formId];
					if (!form?.sectionSupportingDocuments) return;
					form.sectionSupportingDocuments = form.sectionSupportingDocuments.filter(
						(doc) => doc.id !== documentId
					);
				}
			)
			.addMatcher(sustainabilityApi.endpoints.setFormDisabled.matchFulfilled, (state, action) => {
				const profileId = action.meta.arg.originalArgs.profileId;
				const formId = action.meta.arg.originalArgs.request.formId;
				const isDisabled = action.meta.arg.originalArgs.request.isDisabled;
				const form = state.profileForms[profileId]?.[formId];
				if (form) {
					form.isDisabled = isDisabled;
				}
			})
			.addMatcher(
				sustainabilityApi.endpoints.updateSupportingDocumentFilename.matchFulfilled,
				(state, action) => {
					const profileId = action.meta.arg.originalArgs.profileId;
					const formId = action.meta.arg.originalArgs.request.formId;
					const updatedDocument = action.payload;
					const form = state.profileForms[profileId]?.[formId];
					if (!form?.sectionSupportingDocuments || !updatedDocument?.id) return;
					const idx = form.sectionSupportingDocuments.findIndex(
						(doc) => doc.id === updatedDocument.id
					);
					if (idx >= 0) {
						form.sectionSupportingDocuments[idx] = updatedDocument;
					}
				}
			);
	}
});

export const {
	answerQuestion,
	updateForm,
	clearForm,
	clearFormsByProfileId,
	clearAllForms,
	appendSectionSupportingDocument,
	updateAnswerSectionIsApplicable
} = sustainabilityFormsSlice.actions;

export default sustainabilityFormsSlice.reducer;
